import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, X, Search, Check, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface SearchableMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  emptyText?: string;
  className?: string;
  maxDisplayedBadges?: number;
  showSearch?: boolean;
  variant?: 'dropdown' | 'modal';
  modalTitle?: string;
}

export default function SearchableMultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select options...",
  icon,
  emptyText = "No options found",
  className,
  maxDisplayedBadges = 3,
  showSearch = true,
  variant = 'dropdown',
  modalTitle,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = options.filter(option => 
    option.label?.toLowerCase()?.includes(searchQuery?.toLowerCase() || '') ||
    option.value?.toLowerCase()?.includes(searchQuery?.toLowerCase() || '')
  );

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const selectAll = () => {
    onChange(filteredOptions.map(option => option.value));
  };

  const clearAll = () => {
    onChange([]);
  };

  const removeValue = (valueToRemove: string) => {
    onChange(value.filter(v => v !== valueToRemove));
  };

  const selectedOptions = value
    .map(v => options.find(opt => opt.value === v))
    .filter(Boolean) as Option[];

  const displayedBadges = selectedOptions.slice(0, maxDisplayedBadges);
  const remainingCount = selectedOptions.length - maxDisplayedBadges;

  // Handle click outside to close dropdown (only for dropdown variant)
  useEffect(() => {
    if (variant !== 'dropdown') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [open, variant]);

  // Shared options list component
  const renderOptionsList = () => (
    <>
      {filteredOptions.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {emptyText}
        </div>
      ) : (
        <div>
          {filteredOptions.map((option, index) => {
            const isSelected = value.includes(option.value);
            return (
              <div
                key={`${option.value}-${index}`}
                className={`flex items-center px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                onClick={() => toggleOption(option.value)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  className="mr-3 pointer-events-none"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">
                    {option.label}
                  </div>
                  {option.description && (
                    <div className="text-xs text-gray-500 truncate">
                      {option.description}
                    </div>
                  )}
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-blue-600 ml-2" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // Shared action buttons
  const renderActionButtons = () => (
    <div className="flex gap-2 px-4 py-2 border-b flex-shrink-0 bg-gray-50">
      <Button
        variant="outline"
        size="sm"
        onClick={() => selectAll()}
        disabled={filteredOptions.length === 0}
        className="flex-1"
        type="button"
      >
        <Plus className="mr-1 h-3 w-3" />
        Select All ({filteredOptions.length})
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => clearAll()}
        disabled={value.length === 0}
        className="flex-1"
        type="button"
      >
        <X className="mr-1 h-3 w-3" />
        Clear All
      </Button>
    </div>
  );

  // Shared search input
  const renderSearchInput = (autoFocus: boolean = true) => (
    showSearch && (
      <div className="flex items-center border-b px-4 py-2 flex-shrink-0">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <Input
          placeholder="Search options..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex h-10 w-full border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
          autoFocus={autoFocus}
        />
      </div>
    )
  );

  return (
    <div ref={containerRef} className={cn("w-full relative", className)}>
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between h-auto min-h-10 py-2 px-3 border border-gray-300 rounded-md hover:bg-gray-50 bg-white flex items-center text-left focus:outline-none focus:border-gray-400"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center flex-1">
          {icon && <span className="mr-2 flex-shrink-0">{icon}</span>}
          <div className="flex items-center flex-wrap gap-1 flex-1 min-w-0">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <>
                {displayedBadges.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="text-xs flex items-center gap-1 max-w-32"
                  >
                    <span className="truncate">{option.label}</span>
                    <X
                      className="h-3 w-3 hover:bg-muted-foreground/20 rounded-full cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeValue(option.value);
                      }}
                    />
                  </Badge>
                ))}
                {remainingCount > 0 && (
                  <Badge variant="outline" className="text-xs">
                    +{remainingCount} more
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {/* Dropdown variant */}
      {variant === 'dropdown' && open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-gray-50 border border-gray-300 rounded-lg shadow-xl max-h-96 flex flex-col overflow-hidden">
          {renderSearchInput()}
          {renderActionButtons()}
          <div className="flex-1 overflow-y-auto bg-white">
            {renderOptionsList()}
          </div>
          <div className="px-4 py-3 border-t flex-shrink-0">
            <Button
              onClick={() => setOpen(false)}
              className="w-full"
              type="button"
            >
              Done ({value.length} selected)
            </Button>
          </div>
        </div>
      )}

      {/* Modal variant */}
      {variant === 'modal' && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col p-0"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2">
              <DialogTitle>{modalTitle || 'Select Options'}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {value.length} selected
              </p>
            </DialogHeader>

            {renderSearchInput(true)}
            {renderActionButtons()}

            <div className="flex-1 overflow-y-auto min-h-0">
              {renderOptionsList()}
            </div>

            <div className="px-4 py-3 border-t flex-shrink-0 bg-gray-50">
              <Button
                onClick={() => setOpen(false)}
                className="w-full"
                type="button"
              >
                Done ({value.length} selected)
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}
